/**
 * Centralized Wizard Content Registry
 * 
 * Single source of truth for all wizard step content including:
 * - Callout messages and supporting text
 * - Help URLs and documentation links
 * - Support contact information
 * - Standard text snippets
 * 
 * This ensures consistency across the application and makes content
 * updates easier by centralizing all strings in one location.
 */

import { ChipTone } from './actionRegistry';

/**
 * Support Contact Information
 */
export const SUPPORT_CONTACTS = {
  email: 'support@plexus.co',
  supportEmail: 'support@plexlogic.com.au',
  helpText: 'Our team is here for you. Email',
} as const;

/**
 * Documentation and Help URLs
 */
export const HELP_URLS = {
  scrutineerRequirements: 'https://help.plexus.co/en/articles/3784317-draw-scrutineer-requirements',
  saPrizeLimitExclusion: 'https://help.plexus.co/en/articles/4475660-limit-of-1-prize-per-person-sa-exclusion',
  // Add more help URLs as needed
} as const;

/**
 * Callout Configuration
 */
export interface CalloutConfig {
  tone: ChipTone;
  message: string;
  supportingCopy: string | React.ReactNode;
  dataTestId?: string;
}

/**
 * Standard Callout Configurations
 * These can be used directly or as templates for dynamic content
 */
export const CALLOUT_CONFIGS = {
  // Entry Method Step
  scrutineerWarning: {
    tone: 'caution' as ChipTone,
    message: 'Scrutineer requirement',
    dataTestId: 'scrutineer-warning-callout' as string,
    supportingCopy: undefined, // Will be generated with link in component
  },

  schedulingScheduled: {
    tone: 'positive' as ChipTone,
    message: "You're covered:",
    supportingCopy: "Schedule it and we'll run it on time and email you the moment it's done.",
    dataTestId: 'scheduling-covered-callout' as string,
  },

  schedulingDisabled: {
    tone: 'positive' as ChipTone,
    message: 'Runs live with your scrutineer',
    supportingCopy: 'Because this draw needs a scrutineer, it runs now with them present.',
    dataTestId: 'scheduling-disabled-callout' as string,
  },

  // Demo Mode
  demoMode: {
    tone: 'brand' as ChipTone,
    message: 'Demo mode: This is a demonstration. No actual random draws will be performed.',
    dataTestId: 'demo-callout' as string,
    supportingCopy: undefined,
  },

  demoModeConfirmation: {
    tone: 'brand' as ChipTone,
    message: 'Demo mode: This is a demonstration. No actual random draws were performed and no certificates were generated.',
    dataTestId: 'confirmation-demo-callout' as string,
    supportingCopy: undefined,
  },

  // Draw Detail - Failed Draw
  drawExecutionFailed: {
    tone: 'urgent' as ChipTone,
    message: 'Draw Execution Failed',
    dataTestId: 'failed-draw-callout' as string,
    supportingCopy: undefined, // Will be generated with link in component
  },
} as const;

/**
 * Quick Check Messages
 * Reusable text for WizardStepQuickCheck components
 */
export const QUICK_CHECK_MESSAGES = {
  entryMethod: {
    message: 'Quick check:',
    text: "Before you continue, make sure your entry data matches your promotion's eligibility and prize rules. If you'll be uploading a file, remove any personal details you don't need – like email addresses or home addresses.",
    dataTestId: 'important-notice',
  },

  prizeConfiguration: {
    message: 'Quick check:',
    dataTestId: 'prizes-per-entrant-callout',
    text: undefined, // Content will be generated with links in component
  },
} as const;

/**
 * Next Up Messages
 * Standard "Next up" messages for different wizard steps
 */
export const NEXT_UP_MESSAGES = {
  entryMethod: {
    nextStep: "You'll configure your prizes and set up the draw details.",
    dataTestId: 'entry-method-next-steps-callout',
  },
  // Add more as needed
} as const;

/**
 * Text Snippets
 * Reusable text fragments that appear in multiple places
 */
export const TEXT_SNIPPETS = {
  scrutineerWarning: {
    prefix: "If your draw requires a scrutineer, you won't be able to set up and schedule your draw in advance. If you are unsure, review our",
    linkText: 'draw scrutineer requirements',
    suffix: 'before proceeding.',
  },

  saPrizeLimit: {
    prefix: "Some regions don't allow a one-prize-per-person limit when multiple entries are permitted. If that's you, switch the option above or",
    linkText: 'check the requirements',
    suffix: '',
  },

  needHelp: {
    heading: 'Need help?',
    prefix: 'Our team is here for you. Email',
  },

  drawExecutionFailed: {
    prefix: 'Due to unforeseen circumstances, your scheduled draw did not run as planned. Please retry running the draw. If you continue to experience difficulties please email',
    linkText: 'support@plexlogic.com.au',
  },
} as const;

/**
 * Helper function to get support email as mailto link
 */
export function getSupportEmailLink(): string {
  return `mailto:${SUPPORT_CONTACTS.email}`;
}

/**
 * Helper function to get support email text with link formatting
 */
export function getSupportEmailFormatted() {
  return {
    email: SUPPORT_CONTACTS.email,
    href: getSupportEmailLink(),
  };
}

/**
 * Get callout configuration by key
 */
export function getCalloutConfig(key: keyof typeof CALLOUT_CONFIGS): CalloutConfig {
  return CALLOUT_CONFIGS[key] as CalloutConfig;
}

/**
 * Get help URL by key
 */
export function getHelpUrl(key: keyof typeof HELP_URLS): string {
  return HELP_URLS[key];
}

/**
 * Get quick check message configuration by key
 */
export function getQuickCheckConfig(key: keyof typeof QUICK_CHECK_MESSAGES) {
  return QUICK_CHECK_MESSAGES[key];
}

/**
 * Get next up message configuration by key
 */
export function getNextUpConfig(key: keyof typeof NEXT_UP_MESSAGES) {
  return NEXT_UP_MESSAGES[key];
}

/**
 * Get text snippet by key with proper typing
 */
export function getTextSnippet<K extends keyof typeof TEXT_SNIPPETS>(
  key: K
): typeof TEXT_SNIPPETS[K] {
  return TEXT_SNIPPETS[key];
}
